import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Calendar, Plus, Video, Scissors, PlayCircle, FileText, ChevronRight, Mic, Type, X } from 'react-feather';
import TextareaAutosize from 'react-textarea-autosize';
import api from './api';
import { useDarkMode } from './DarkModeContext';
import WorkspaceHomeAutomation from './WorkspaceHomeAutomation';

const SYSTEM_UI_FONT = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const HOME_PREVIEW_COUNT = 4;
const CALENDAR_DAYS = 14;
const PROJECTS_PAGE_SIZE = 20;

const formatDateKey = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const startOfLocalDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
};

function scriptPreviewText(script) {
    if (!script) return '';
    const version = script.selectedVersion || 'main';
    const body =
        version === 'alt' ? (script.contentAlt || script.content) :
        version === 'third' ? (script.contentThird || script.content) :
        script.content;
    return (body || script.hook || script.description || '').trim();
}

function HomeProjectCard({ script, openingProjectId, onOpen }) {
    const preview = scriptPreviewText(script);
    const isOpening = String(openingProjectId) === String(script._id);
    const isText = script.type === 'text';
    return (
        <button
            key={script._id}
            type="button"
            className={`home-project-card ${isText ? 'home-project-card--text' : 'home-project-card--voiceover'}`}
            disabled={!!openingProjectId}
            onClick={() => onOpen?.(script)}
            style={{ opacity: openingProjectId && !isOpening ? 0.55 : 1 }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span className={`home-project-type-badge ${isText ? 'home-project-type-badge--text' : 'home-project-type-badge--voiceover'}`}>
                    {isText ? <Type size={14} /> : <Mic size={14} />}
                    {isText ? 'text' : 'voiceover'}
                </span>
                <ChevronRight size={16} style={{ opacity: 0.5, flexShrink: 0 }} />
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="home-project-preview">
                        {preview || 'Empty project'}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                            {script.status || 'draft'}
                        </span>
                        {script.voiceoverUrl && (
                            <span style={{ fontSize: 11, color: '#8b5cf6' }}>Voiceover</span>
                        )}
                        {script.videoUrl && (
                            <span style={{ fontSize: 11, color: '#10b981' }}>Video</span>
                        )}
                        {isOpening && (
                            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Opening…</span>
                        )}
                    </div>
                </div>
                {(script.videoUrl || script.videoThumbnailUrl) && (
                    <div style={{
                        flex: '0 0 56px',
                        width: 56,
                        aspectRatio: '9/16',
                        borderRadius: 6,
                        overflow: 'hidden',
                        background: 'var(--input-border)',
                        position: 'relative',
                    }}>
                        <video
                            src={script.videoUrl || undefined}
                            poster={script.videoThumbnailUrl || undefined}
                            muted
                            playsInline
                            preload={script.videoThumbnailUrl ? 'none' : 'metadata'}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                    </div>
                )}
            </div>
        </button>
    );
}

function MediaThumb({ url, poster, title, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={title || ''}
            style={{
                flex: '0 0 auto',
                width: 120,
                border: '1px solid var(--input-border)',
                borderRadius: 8,
                overflow: 'hidden',
                background: 'var(--input-bg)',
                padding: 0,
                cursor: onClick ? 'pointer' : 'default',
                textAlign: 'left',
            }}
        >
            <div style={{ width: '100%', aspectRatio: '9/16', background: 'var(--input-border)', position: 'relative' }}>
                {url || poster ? (
                    <video
                        src={url || undefined}
                        poster={poster || undefined}
                        muted
                        playsInline
                        preload={poster ? 'none' : 'metadata'}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
                        <Video size={20} />
                    </div>
                )}
                {title ? (
                    <div
                        style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            padding: '28px 8px 8px',
                            background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 55%, transparent 100%)',
                            color: '#fff',
                            fontSize: 12,
                            fontWeight: 500,
                            lineHeight: 1.3,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {title}
                    </div>
                ) : null}
            </div>
        </button>
    );
}

export default function WorkspaceHomeTab({
    currentWorkspace,
    updateWorkspace,
    videos = [],
    workspaceCuts = [],
    generatedVideos = [],
    onNavigateTab,
    onOpenProject,
    getlateConnectedAccounts = { instagram: false, tiktok: false },
    onScheduleSuccess,
    handleGetlatePublish,
    getlatePublishing = new Set(),
    clearPublishingForVideo,
}) {
    const { darkMode } = useDarkMode();
    const workspaceId = currentWorkspace?._id;

    const [scheduledPosts, setScheduledPosts] = useState([]);
    const [projects, setProjects] = useState([]);
    const [homeCuts, setHomeCuts] = useState([]);
    const [loadingCalendar, setLoadingCalendar] = useState(false);
    const [loadingProjects, setLoadingProjects] = useState(false);
    const [notes, setNotes] = useState(currentWorkspace?.workspaceNotes || '');
    const [notesSaving, setNotesSaving] = useState(false);
    const [notesSavedAt, setNotesSavedAt] = useState(null);
    const [openingProjectId, setOpeningProjectId] = useState(null);
    const [showAllProjectsOverlay, setShowAllProjectsOverlay] = useState(false);
    const [allProjects, setAllProjects] = useState([]);
    const [allProjectsPage, setAllProjectsPage] = useState(1);
    const [allProjectsHasMore, setAllProjectsHasMore] = useState(true);
    const [loadingAllProjects, setLoadingAllProjects] = useState(false);
    const [loadingMoreProjects, setLoadingMoreProjects] = useState(false);
    const projectsScrollRef = useRef(null);
    const projectsLoadMoreRef = useRef(null);
    const notesSaveTimer = useRef(null);
    const notesBaseline = useRef(currentWorkspace?.workspaceNotes || '');

    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [scheduleVideoId, setScheduleVideoId] = useState(null);
    const [scheduleDate, setScheduleDate] = useState('');
    const [scheduleTime, setScheduleTime] = useState('');
    const [scheduleCaption, setScheduleCaption] = useState('');
    const [scheduleIg, setScheduleIg] = useState(true);
    const [scheduleTt, setScheduleTt] = useState(true);
    const [scheduleError, setScheduleError] = useState('');
    const [postPreview, setPostPreview] = useState(null);

    const calendarDays = useMemo(() => {
        const today = startOfLocalDay(new Date());
        return Array.from({ length: CALENDAR_DAYS }, (_, i) => {
            const d = new Date(today);
            d.setDate(today.getDate() + i);
            return d;
        });
    }, []);

    const postsByDate = useMemo(() => {
        const map = {};
        scheduledPosts.forEach((p) => {
            const key = formatDateKey(new Date(p.scheduledAt));
            if (!map[key]) map[key] = [];
            map[key].push(p);
        });
        return map;
    }, [scheduledPosts]);

    const refreshScheduledPosts = useCallback(async () => {
        if (!workspaceId) return;
        setLoadingCalendar(true);
        try {
            const from = startOfLocalDay(new Date()).toISOString();
            const to = new Date(startOfLocalDay(new Date()));
            to.setDate(to.getDate() + CALENDAR_DAYS);
            to.setHours(23, 59, 59, 999);
            const data = await api.getScheduledPosts(workspaceId, from, to.toISOString());
            setScheduledPosts(Array.isArray(data) ? data : []);
        } catch {
            setScheduledPosts([]);
        } finally {
            setLoadingCalendar(false);
        }
    }, [workspaceId]);

    const fetchProjects = useCallback(async () => {
        if (!workspaceId) return;
        setLoadingProjects(true);
        try {
            const res = await api.getWorkspaceScripts(workspaceId, { lite: true, limit: 8 });
            const list = Array.isArray(res.data) ? res.data : (res.data?.scripts || []);
            setProjects(list);
        } catch {
            setProjects([]);
        } finally {
            setLoadingProjects(false);
        }
    }, [workspaceId]);

    const fetchAllProjectsPage = useCallback(async (page, { append } = { append: false }) => {
        if (!workspaceId) return;
        if (append) setLoadingMoreProjects(true);
        else setLoadingAllProjects(true);
        try {
            const res = await api.getWorkspaceScripts(workspaceId, {
                lite: true,
                page,
                limit: PROJECTS_PAGE_SIZE,
            });
            const payload = res.data;
            const list = Array.isArray(payload) ? payload : (payload?.scripts || []);
            const hasMore = Array.isArray(payload)
                ? list.length >= PROJECTS_PAGE_SIZE
                : !!payload?.hasMore;
            setAllProjects((prev) => {
                if (!append) return list;
                const seen = new Set(prev.map((s) => String(s._id)));
                return [...prev, ...list.filter((s) => !seen.has(String(s._id)))];
            });
            setAllProjectsPage(page);
            setAllProjectsHasMore(hasMore);
        } catch {
            if (!append) setAllProjects([]);
            setAllProjectsHasMore(false);
        } finally {
            if (append) setLoadingMoreProjects(false);
            else setLoadingAllProjects(false);
        }
    }, [workspaceId]);

    const openAllProjectsOverlay = useCallback(() => {
        setShowAllProjectsOverlay(true);
        setAllProjects([]);
        setAllProjectsPage(1);
        setAllProjectsHasMore(true);
        fetchAllProjectsPage(1, { append: false });
    }, [fetchAllProjectsPage]);

    const closeAllProjectsOverlay = useCallback(() => {
        setShowAllProjectsOverlay(false);
        setLoadingAllProjects(false);
        setLoadingMoreProjects(false);
    }, []);

    const handleOpenProject = useCallback(async (script) => {
        if (!script?._id || openingProjectId) return;
        setOpeningProjectId(script._id);
        try {
            if (typeof onOpenProject === 'function') {
                await onOpenProject(script._id, script);
            } else {
                onNavigateTab?.('studio');
            }
            setShowAllProjectsOverlay(false);
        } finally {
            setOpeningProjectId(null);
        }
    }, [openingProjectId, onOpenProject, onNavigateTab]);

    useEffect(() => {
        if (!showAllProjectsOverlay || !allProjectsHasMore || loadingAllProjects || loadingMoreProjects) return;
        const root = projectsScrollRef.current;
        const target = projectsLoadMoreRef.current;
        if (!root || !target) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    fetchAllProjectsPage(allProjectsPage + 1, { append: true });
                }
            },
            { root, rootMargin: '160px', threshold: 0 }
        );
        observer.observe(target);
        return () => observer.disconnect();
    }, [
        showAllProjectsOverlay,
        allProjectsHasMore,
        loadingAllProjects,
        loadingMoreProjects,
        allProjectsPage,
        fetchAllProjectsPage,
        allProjects.length,
    ]);

    useEffect(() => {
        if (!showAllProjectsOverlay) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') closeAllProjectsOverlay();
        };
        window.addEventListener('keydown', onKeyDown);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = prevOverflow;
        };
    }, [showAllProjectsOverlay, closeAllProjectsOverlay]);

    const fetchHomeCuts = useCallback(async () => {
        if (!workspaceId) return;
        if (workspaceCuts?.length > 0) {
            setHomeCuts(workspaceCuts.slice(0, HOME_PREVIEW_COUNT));
            return;
        }
        try {
            const res = await api.getWorkspaceCuts(workspaceId, { page: 1, pageSize: HOME_PREVIEW_COUNT });
            const items = res.data?.items || (Array.isArray(res.data) ? res.data : []);
            setHomeCuts(items.slice(0, HOME_PREVIEW_COUNT));
        } catch {
            setHomeCuts([]);
        }
    }, [workspaceId, workspaceCuts]);

    useEffect(() => {
        refreshScheduledPosts();
        fetchProjects();
        fetchHomeCuts();
    }, [refreshScheduledPosts, fetchProjects, fetchHomeCuts]);

    useEffect(() => {
        const next = currentWorkspace?.workspaceNotes || '';
        setNotes(next);
        notesBaseline.current = next;
    }, [currentWorkspace?._id, currentWorkspace?.workspaceNotes]);

    const persistNotes = useCallback(async (value) => {
        if (!workspaceId || !updateWorkspace) return;
        if (value === notesBaseline.current) return;
        setNotesSaving(true);
        try {
            await updateWorkspace(workspaceId, { workspaceNotes: value }, { skipLoading: true });
            notesBaseline.current = value;
            setNotesSavedAt(Date.now());
        } catch (err) {
            console.warn('Failed to save workspace notes:', err);
        } finally {
            setNotesSaving(false);
        }
    }, [workspaceId, updateWorkspace]);

    const onNotesChange = (e) => {
        const value = e.target.value;
        setNotes(value);
        if (notesSaveTimer.current) clearTimeout(notesSaveTimer.current);
        notesSaveTimer.current = setTimeout(() => persistNotes(value), 700);
    };

    useEffect(() => () => {
        if (notesSaveTimer.current) clearTimeout(notesSaveTimer.current);
    }, []);

    const openScheduleModal = () => {
        setScheduleError('');
        setScheduleVideoId(null);
        setScheduleCaption('');
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setScheduleDate(formatDateKey(tomorrow));
        setScheduleTime('10:00');
        setScheduleIg(!!getlateConnectedAccounts.instagram);
        setScheduleTt(!!getlateConnectedAccounts.tiktok);
        setShowScheduleModal(true);
    };

    const submitSchedule = async () => {
        setScheduleError('');
        const video = generatedVideos.find((v) => String(v._id) === String(scheduleVideoId));
        if (!video) {
            setScheduleError('Pick a generated video to schedule.');
            return;
        }
        if (!scheduleDate || !scheduleTime) {
            setScheduleError('Pick a date and time.');
            return;
        }
        const platforms = [];
        if (scheduleIg && getlateConnectedAccounts.instagram) platforms.push('instagram');
        if (scheduleTt && getlateConnectedAccounts.tiktok) platforms.push('tiktok');
        if (platforms.length === 0) {
            setScheduleError('Connect Instagram or TikTok first (Profile / Members).');
            return;
        }
        if (typeof handleGetlatePublish !== 'function') {
            setScheduleError('Scheduling is unavailable right now.');
            return;
        }
        const scheduleAt = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
        const opts = { caption: scheduleCaption || video.description || '', scheduleAt };
        const results = await Promise.all(
            platforms.map((platform) => handleGetlatePublish(video, platform, false, opts))
        );
        if (typeof clearPublishingForVideo === 'function') clearPublishingForVideo(video._id);
        const failed = results.filter((r) => r && !r.success);
        const succeeded = results.filter((r) => r && r.success);
        if (failed.length > 0 && succeeded.length === 0) {
            setScheduleError(failed.map((r) => `${r.platform}: ${r.error || 'failed'}`).join('. '));
            return;
        }
        setShowScheduleModal(false);
        await refreshScheduledPosts();
        if (typeof onScheduleSuccess === 'function') onScheduleSuccess(succeeded);
    };

    const rawPreview = (videos || []).slice(0, HOME_PREVIEW_COUNT);
    const cutsPreview = (homeCuts || []).slice(0, HOME_PREVIEW_COUNT);
    const generatedPreview = (generatedVideos || []).slice(0, HOME_PREVIEW_COUNT);
    const isScheduling = scheduleVideoId && getlatePublishing.has(scheduleVideoId);

    const sectionTitle = (label, tab, icon, onViewAll) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600 }}>
                {icon}
                {label}
            </h4>
            {(onViewAll || tab) && (
                <button
                    type="button"
                    onClick={() => (onViewAll ? onViewAll() : onNavigateTab?.(tab))}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--text-secondary)',
                        cursor: 'pointer',
                        fontSize: 13,
                        padding: 0,
                    }}
                >
                    View all <ChevronRight size={14} />
                </button>
            )}
        </div>
    );

    return (
        <div className="workspace-home-tab" style={{ maxWidth: 960, fontFamily: SYSTEM_UI_FONT }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0 }}>Home</h3>
                <button
                    type="button"
                    className="main-button"
                    onClick={openScheduleModal}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 14px',
                        borderRadius: 6,
                        border: 'none',
                        backgroundColor: '#E4405F',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 500,
                    }}
                >
                    <Plus size={16} />
                    Schedule post
                </button>
            </div>

            {/* Calendar strip */}
            <section style={{ marginBottom: 28 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600 }}>
                        <Calendar size={16} />
                        Upcoming
                    </h4>
                    {workspaceId && (
                        <a
                            href={`/calendar/${workspaceId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}
                        >
                            Full calendar
                        </a>
                    )}
                </div>
                {loadingCalendar ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Loading schedule…</p>
                ) : (
                    <div
                        style={{
                            display: 'flex',
                            gap: 10,
                            overflowX: 'auto',
                            paddingBottom: 8,
                            WebkitOverflowScrolling: 'touch',
                        }}
                    >
                        {calendarDays.map((d, idx) => {
                            const key = formatDateKey(d);
                            const dayPosts = postsByDate[key] || [];
                            const isToday = idx === 0;
                            return (
                                <div
                                    key={key}
                                    style={{
                                        flex: '0 0 112px',
                                        minHeight: 110,
                                        border: `1px solid ${isToday ? '#E4405F' : 'var(--input-border)'}`,
                                        borderRadius: 8,
                                        padding: 10,
                                        background: darkMode ? 'var(--input-bg)' : '#fafafa',
                                    }}
                                >
                                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                                        {isToday ? 'Today' : d.toLocaleDateString(undefined, { weekday: 'short' })}
                                    </div>
                                    <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: 'var(--text-color)' }}>
                                        {d.getDate()}
                                    </div>
                                    {dayPosts.length === 0 ? (
                                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>—</div>
                                    ) : (
                                        dayPosts.slice(0, 3).map((p) => (
                                            <button
                                                key={p._id}
                                                type="button"
                                                onClick={() => setPostPreview(p)}
                                                style={{
                                                    display: 'block',
                                                    width: '100%',
                                                    textAlign: 'left',
                                                    border: 'none',
                                                    borderRadius: 4,
                                                    padding: '3px 5px',
                                                    marginBottom: 4,
                                                    fontSize: 11,
                                                    cursor: 'pointer',
                                                    background: p.platform === 'instagram' ? 'rgba(228,64,95,0.18)' : 'rgba(0,0,0,0.08)',
                                                    color: 'var(--text-color)',
                                                }}
                                            >
                                                {p.platform === 'instagram' ? 'IG' : 'TT'}{' '}
                                                {new Date(p.scheduledAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                            </button>
                                        ))
                                    )}
                                    {dayPosts.length > 3 && (
                                        <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>+{dayPosts.length - 3} more</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            <WorkspaceHomeAutomation
                currentWorkspace={currentWorkspace}
                updateWorkspace={updateWorkspace}
                getlateConnectedAccounts={getlateConnectedAccounts}
                onOpenProject={onOpenProject}
            />

            {/* Projects (VideoScripts) */}
            <section style={{ marginBottom: 28 }}>
                {sectionTitle('Recent projects', null, <FileText size={16} />, openAllProjectsOverlay)}
                {loadingProjects ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Loading projects…</p>
                ) : projects.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>
                        No projects yet. Generate scripts in Studio.
                    </p>
                ) : (
                    <div className="home-projects-grid">
                        {projects.map((script) => (
                            <HomeProjectCard
                                key={script._id}
                                script={script}
                                openingProjectId={openingProjectId}
                                onOpen={handleOpenProject}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* Media previews */}
            <section style={{ marginBottom: 28 }}>
                {sectionTitle('Raw videos', 'raw', <Video size={16} />)}
                {rawPreview.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '0 0 20px' }}>No raw videos yet.</p>
                ) : (
                    <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8, marginBottom: 20 }}>
                        {rawPreview.map((v) => (
                            <MediaThumb
                                key={v._id}
                                url={v.url}
                                poster={v.thumbnailUrl}
                                title={v.name || v.title || 'Video'}
                                onClick={() => onNavigateTab?.('raw')}
                            />
                        ))}
                    </div>
                )}

                {sectionTitle('Cuts', 'cuts', <Scissors size={16} />)}
                {cutsPreview.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '0 0 20px' }}>No cuts yet.</p>
                ) : (
                    <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8, marginBottom: 20 }}>
                        {cutsPreview.map((c) => (
                            <MediaThumb
                                key={c._id}
                                url={c.url || c.videoUrl}
                                poster={c.thumbnailUrl}
                                title={c.name || c.title || 'Cut'}
                                onClick={() => onNavigateTab?.('cuts')}
                            />
                        ))}
                    </div>
                )}

                {sectionTitle('Generated videos', 'generated', <PlayCircle size={16} />)}
                {generatedPreview.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>No generated videos yet.</p>
                ) : (
                    <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8 }}>
                        {generatedPreview.map((v) => (
                            <MediaThumb
                                key={v._id}
                                url={v.url}
                                poster={v.thumbnailUrl}
                                title={v.title || 'Generated'}
                                onClick={() => onNavigateTab?.('generated')}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* Workspace notes */}
            <section style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Workspace notes</h4>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {notesSaving ? 'Saving…' : notesSavedAt ? 'Saved' : 'Used when generating scripts'}
                    </span>
                </div>
                <TextareaAutosize
                    value={notes}
                    onChange={onNotesChange}
                    onBlur={() => persistNotes(notes)}
                    minRows={5}
                    placeholder="Dump ideas, facts, talking points, offers… anything useful for future scripts."
                    style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: 12,
                        borderRadius: 8,
                        border: '1px solid var(--input-border)',
                        background: 'var(--input-bg)',
                        color: 'var(--input-text)',
                        fontFamily: SYSTEM_UI_FONT,
                        fontSize: 14,
                        lineHeight: 1.5,
                        resize: 'vertical',
                    }}
                />
            </section>

            {/* Schedule modal */}
            {showScheduleModal && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10000,
                        padding: 16,
                    }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setShowScheduleModal(false);
                    }}
                >
                    <div
                        style={{
                            background: darkMode ? '#2A2B2F' : '#fff',
                            borderRadius: 8,
                            padding: 24,
                            maxWidth: 520,
                            width: '100%',
                            maxHeight: '90vh',
                            overflow: 'auto',
                        }}
                    >
                        <h3 style={{ margin: '0 0 16px' }}>Schedule post</h3>

                        <label style={{ display: 'block', marginBottom: 8, fontSize: 14 }}>Generated video</label>
                        {generatedVideos.length === 0 ? (
                            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                                No generated videos available. Create one in Studio first.
                            </p>
                        ) : (
                            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', marginBottom: 16, paddingBottom: 4 }}>
                                {generatedVideos.slice(0, 24).map((v) => {
                                    const selected = String(v._id) === String(scheduleVideoId);
                                    return (
                                        <button
                                            key={v._id}
                                            type="button"
                                            onClick={() => {
                                                setScheduleVideoId(v._id);
                                                setScheduleCaption(v.description || '');
                                            }}
                                            style={{
                                                flex: '0 0 88px',
                                                border: selected ? '2px solid #E4405F' : '1px solid var(--input-border)',
                                                borderRadius: 8,
                                                padding: 0,
                                                overflow: 'hidden',
                                                background: 'var(--input-bg)',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <div style={{ width: '100%', aspectRatio: '9/16', background: 'var(--input-border)' }}>
                                                <video
                                                    src={v.url}
                                                    poster={v.thumbnailUrl || undefined}
                                                    muted
                                                    playsInline
                                                    preload={v.thumbnailUrl ? 'none' : 'metadata'}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                />
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        <label style={{ display: 'block', marginBottom: 8, fontSize: 14 }}>Caption</label>
                        <TextareaAutosize
                            value={scheduleCaption}
                            onChange={(e) => setScheduleCaption(e.target.value)}
                            minRows={2}
                            maxRows={5}
                            style={{
                                width: '100%',
                                boxSizing: 'border-box',
                                padding: 8,
                                borderRadius: 4,
                                border: '1px solid var(--input-border)',
                                background: 'var(--input-bg)',
                                color: 'var(--input-text)',
                                fontFamily: SYSTEM_UI_FONT,
                                fontSize: 14,
                                marginBottom: 16,
                            }}
                        />

                        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>Date</label>
                                <input
                                    type="date"
                                    value={scheduleDate}
                                    onChange={(e) => setScheduleDate(e.target.value)}
                                    style={{
                                        padding: '6px 8px',
                                        borderRadius: 4,
                                        border: '1px solid var(--input-border)',
                                        background: 'var(--input-bg)',
                                        color: 'var(--input-text)',
                                    }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>Time</label>
                                <input
                                    type="time"
                                    value={scheduleTime}
                                    onChange={(e) => setScheduleTime(e.target.value)}
                                    style={{
                                        padding: '6px 8px',
                                        borderRadius: 4,
                                        border: '1px solid var(--input-border)',
                                        background: 'var(--input-bg)',
                                        color: 'var(--input-text)',
                                    }}
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 14, opacity: getlateConnectedAccounts.instagram ? 1 : 0.55 }}>
                                <input
                                    type="checkbox"
                                    checked={scheduleIg}
                                    disabled={!getlateConnectedAccounts.instagram}
                                    onChange={(e) => setScheduleIg(e.target.checked)}
                                />
                                Instagram {!getlateConnectedAccounts.instagram && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>(connect first)</span>}
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, opacity: getlateConnectedAccounts.tiktok ? 1 : 0.55 }}>
                                <input
                                    type="checkbox"
                                    checked={scheduleTt}
                                    disabled={!getlateConnectedAccounts.tiktok}
                                    onChange={(e) => setScheduleTt(e.target.checked)}
                                />
                                TikTok {!getlateConnectedAccounts.tiktok && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>(connect first)</span>}
                            </label>
                        </div>

                        {scheduleError && (
                            <p style={{ color: '#e53935', fontSize: 13, marginBottom: 12 }}>{scheduleError}</p>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                            <button
                                type="button"
                                onClick={() => setShowScheduleModal(false)}
                                style={{
                                    padding: '8px 14px',
                                    borderRadius: 4,
                                    border: '1px solid var(--input-border)',
                                    background: 'var(--input-bg)',
                                    color: 'var(--text-color)',
                                    cursor: 'pointer',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={submitSchedule}
                                disabled={isScheduling || !scheduleVideoId}
                                style={{
                                    padding: '8px 14px',
                                    borderRadius: 4,
                                    border: 'none',
                                    background: '#E4405F',
                                    color: '#fff',
                                    cursor: isScheduling || !scheduleVideoId ? 'not-allowed' : 'pointer',
                                    opacity: isScheduling || !scheduleVideoId ? 0.6 : 1,
                                }}
                            >
                                {isScheduling ? 'Scheduling…' : 'Schedule'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Post preview */}
            {postPreview && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10000,
                        padding: 16,
                    }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setPostPreview(null);
                    }}
                >
                    <div
                        style={{
                            background: darkMode ? '#2A2B2F' : '#fff',
                            borderRadius: 8,
                            padding: 20,
                            maxWidth: 400,
                            width: '100%',
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                            <strong>
                                {postPreview.platform === 'instagram' ? 'Instagram' : 'TikTok'} ·{' '}
                                {new Date(postPreview.scheduledAt).toLocaleString()}
                            </strong>
                            <button type="button" onClick={() => setPostPreview(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-color)' }}>✕</button>
                        </div>
                        {postPreview.videoUrl ? (
                            <video
                                src={postPreview.videoUrl}
                                controls
                                playsInline
                                style={{ width: '100%', maxHeight: 360, borderRadius: 6, marginBottom: 12, background: '#000' }}
                            />
                        ) : null}
                        <p style={{ margin: 0, fontSize: 14, whiteSpace: 'pre-wrap', color: 'var(--text-color)' }}>
                            {postPreview.caption || 'No caption'}
                        </p>
                    </div>
                </div>
            )}

            {/* All projects overlay */}
            {showAllProjectsOverlay && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.55)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10020,
                        padding: 16,
                    }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) closeAllProjectsOverlay();
                    }}
                >
                    <div
                        style={{
                            background: darkMode ? '#1f2023' : '#fff',
                            borderRadius: 12,
                            border: '1px solid var(--input-border)',
                            width: 'min(920px, 100%)',
                            maxHeight: 'min(84vh, 900px)',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            boxShadow: '0 16px 48px rgba(0,0,0,0.28)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                            padding: '16px 18px',
                            borderBottom: '1px solid var(--input-border)',
                            flexShrink: 0,
                        }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: 18, color: 'var(--text-color)' }}>All projects</h3>
                                <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                                    Click a project to open it in Studio
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeAllProjectsOverlay}
                                aria-label="Close"
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    color: 'var(--text-color)',
                                    padding: 6,
                                    display: 'inline-flex',
                                    borderRadius: 6,
                                }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div
                            ref={projectsScrollRef}
                            style={{
                                flex: 1,
                                overflowY: 'auto',
                                padding: 18,
                                WebkitOverflowScrolling: 'touch',
                            }}
                        >
                            {loadingAllProjects && allProjects.length === 0 ? (
                                <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>Loading projects…</p>
                            ) : allProjects.length === 0 ? (
                                <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>
                                    No projects yet. Generate scripts in Studio.
                                </p>
                            ) : (
                                <>
                                    <div className="home-projects-grid">
                                        {allProjects.map((script) => (
                                            <HomeProjectCard
                                                key={script._id}
                                                script={script}
                                                openingProjectId={openingProjectId}
                                                onOpen={handleOpenProject}
                                            />
                                        ))}
                                    </div>
                                    <div ref={projectsLoadMoreRef} style={{ height: 1 }} />
                                    {(loadingMoreProjects || allProjectsHasMore) && (
                                        <p style={{
                                            margin: '16px 0 0',
                                            textAlign: 'center',
                                            fontSize: 13,
                                            color: 'var(--text-secondary)',
                                        }}>
                                            {loadingMoreProjects ? 'Loading more…' : ''}
                                        </p>
                                    )}
                                    {!allProjectsHasMore && allProjects.length > 0 && (
                                        <p style={{
                                            margin: '16px 0 0',
                                            textAlign: 'center',
                                            fontSize: 12,
                                            color: 'var(--text-secondary)',
                                        }}>
                                            End of projects
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
